import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Calendar, Mail } from 'lucide-react';
import { motion } from 'framer-motion';
import { api } from '../api';
import NewsletterSignup from './NewsletterSignup';

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-GB', { month: 'long', day: 'numeric', year: 'numeric' });
  } catch {
    return '';
  }
};

const NewsletterArchive = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api
      .getNewsletterArchive()
      .then((data) => {
        if (!mounted) return;
        setItems(Array.isArray(data) ? data : []);
      })
      .catch(() => mounted && setItems([]))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    document.title = 'Newsletter archive — Grouth';
    const prev = document.head.querySelector('meta[name="description"]')?.getAttribute('content');
    let metaEl = document.head.querySelector('meta[name="description"]');
    if (!metaEl) {
      metaEl = document.createElement('meta');
      metaEl.setAttribute('name', 'description');
      document.head.appendChild(metaEl);
    }
    metaEl.setAttribute('content', 'Past Grouth newsletter editions. Read what we sent to subscribers — straight thinking on growth marketing, adtech and brand strategy.');
    return () => {
      if (prev != null) metaEl.setAttribute('content', prev);
    };
  }, []);

  return (
    <div className="bg-[#FDFEFA] min-h-screen" data-testid="newsletter-archive-page">
      {/* Header */}
      <div className="bg-[#242326] pt-24 pb-16 md:pb-20">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12 lg:px-16">
          <button
            onClick={() => navigate('/insights')}
            data-testid="newsletter-back-insights"
            className="inline-flex items-center gap-2 text-white/50 text-sm font-['Inter'] mb-8 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Insights
          </button>
          <motion.h1
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="text-white text-4xl md:text-6xl lg:text-[72px] font-light leading-[1.05] tracking-tight font-['Inter'] mb-4"
          >
            Newsletter archive
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="text-white/60 text-lg md:text-xl font-['Lato'] font-light max-w-2xl"
          >
            Every issue we&rsquo;ve sent to subscribers, in one place. No paywalls, no fluff.
          </motion.p>
        </div>
      </div>

      {/* List */}
      <div className="max-w-[1100px] mx-auto px-6 md:px-12 lg:px-16 py-12 md:py-16">
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-7 h-7 border-2 border-[#83BF42] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-16" data-testid="newsletter-archive-empty">
            <Mail className="w-10 h-10 text-[#83BF42]/40 mx-auto mb-4" />
            <p className="text-[#242326] text-xl font-['Inter'] font-light">
              The first issue is on its way.
            </p>
            <p className="text-[#4A4A48] text-sm font-['Lato'] mt-2 max-w-md mx-auto">
              Subscribe below and you&rsquo;ll be among the first to receive it.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-[#242326]/8">
            {items.map((b, i) => (
              <motion.li
                key={b.id}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-30px' }}
                transition={{ duration: 0.4, delay: (i % 6) * 0.05 }}
                className="py-6 md:py-7 group cursor-pointer"
                onClick={() => navigate(`/newsletter/${b.id}`)}
                data-testid={`newsletter-archive-item-${b.id}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-2">
                  <h2 className="text-[#242326] text-xl md:text-2xl font-medium font-['Inter'] leading-snug group-hover:text-[#83BF42] transition-colors">
                    {b.subject}
                  </h2>
                  <span className="text-[#B8B8B8] text-xs font-['Lato'] flex items-center gap-1 shrink-0 sm:ml-6">
                    <Calendar className="w-3 h-3" />
                    {formatDate(b.completed_at || b.created_at)}
                  </span>
                </div>
                <p className="text-[#4A4A48] text-sm md:text-base font-['Lato'] font-light leading-relaxed mt-2 line-clamp-2">
                  {(b.body || '').split('\n').slice(0, 3).join(' ')}
                </p>
                <div className="mt-3 inline-flex items-center gap-1.5 text-[#83BF42] text-sm font-['Inter'] opacity-70 group-hover:opacity-100 group-hover:gap-2.5 transition-all">
                  Read issue <ArrowRight className="w-3.5 h-3.5" />
                </div>
              </motion.li>
            ))}
          </ul>
        )}

        <div className="mt-16 pt-10 border-t border-[#242326]/8">
          <NewsletterSignup />
        </div>
      </div>
    </div>
  );
};

export default NewsletterArchive;
